import ConfirmAction from '@/components/confirm-action';
import Avatar from '@/components/linear/avatar';
import EmptyState from '@/components/linear/empty-state';
import PageToolbar from '@/components/linear/page-toolbar';
import { membersPath, settingsMemberPath } from '@/lib/linear-paths';
import type { SharedPageProps } from '@/types/shared';
import { Form, router, usePage } from '@inertiajs/react';

interface Membership {
  id: string;
  role: string;
  user?: { id: string; name: string; emailAddress: string; avatarColor?: string } | null;
}

interface Props {
  memberships?: Membership[];
  error?: string | null;
  canManageMembers?: boolean;
}

export default function Index({ memberships = [], error, canManageMembers = false }: Props) {
  const { currentUser, workspaceAdmin } = usePage<SharedPageProps>().props;
  const canManage = canManageMembers || workspaceAdmin === true;

  return (
    <div className="ln-page">
      <PageToolbar title="Members" showNew={false} />
      <div className="ln-page-scroll" style={{ padding: 24, maxWidth: 720 }}>
        {canManage ? (
          <Form
            method="post"
            action={membersPath()}
            className="ln-settings-row"
            style={{ marginBottom: 16 }}
          >
            {({ processing, errors }) => (
              <>
                <input
                  className="ln-filter-input"
                  type="email"
                  name="emailAddress"
                  placeholder="Email"
                  aria-label="Email"
                  required
                  style={{ flex: 1 }}
                />
                <input
                  className="ln-filter-input"
                  type="text"
                  name="name"
                  placeholder="Name"
                  aria-label="Name"
                  style={{ flex: 1 }}
                />
                <select
                  className="ln-filter-input"
                  name="role"
                  defaultValue="member"
                  aria-label="Role"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
                <button type="submit" className="ln-primary-btn" disabled={processing}>
                  Invite
                </button>
                {errors.emailAddress ? (
                  <div className="ln-auth-error">{errors.emailAddress}</div>
                ) : null}
                {error ? <div className="ln-auth-error">{error}</div> : null}
              </>
            )}
          </Form>
        ) : null}
        {memberships.length === 0 ? (
          <EmptyState title="No members" description="Invite teammates from workspace settings." />
        ) : (
          memberships.map((membership) => (
            <div key={membership.id} className="ln-row" style={{ height: 52 }}>
              <Avatar name={membership.user?.name} color={membership.user?.avatarColor} size={22} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="ln-row-title">{membership.user?.name ?? 'Unknown'}</div>
                <div className="ln-inbox-sub">{membership.user?.emailAddress}</div>
              </div>
              <span className="ln-label">{membership.role}</span>
              {canManage && membership.user?.id !== currentUser?.id ? (
                <ConfirmAction
                  title="Remove member?"
                  confirmLabel="Remove"
                  onConfirm={() => router.delete(settingsMemberPath(membership.id))}
                >
                  <button type="button" className="ln-toolbar-btn" style={{ marginLeft: 8 }}>
                    Remove
                  </button>
                </ConfirmAction>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
